/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import { Box, BoxPart } from 'components/box';
import { navigate } from 'libs/router';

import TabsSection from 'components/tabs/tabs-section/TabsSection';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import { DataGatheringPlaceholder } from 'components/data-gathering-placeholder/DataGatheringPlaceholder';

import ProfileAdminPanel from 'modules/admin/components/profile-admin-panel/ProfileAdminPanel';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import InfluencerChannelsCard from './InfluencerChannelsCard';
import InfluencerProfileSummary from './influencer-summary/InfluencerProfileSummary';
import InfluencerAboutTab from './about/InfluencerAboutTab';
import InfluencerCampaignHistoryTab from './history/InfluencerCampaignHistoryTab';
import InfluencerProfileTabsSection from './profile-tabs-section/InfluencerProfileTabsSection';
import InfluencerAudienceData from './influencer-audience-data/InfluencerAudienceData';
import InfluencerProfileRelatedInfluencers from './related-influencers/InfluencerProfileRelatedInfluencers';

import { isLightweightProfileMaintainerSelector } from 'selectors/lightWeightProfileMaintainers';

import { fetchInfluencerProfile, resetInfluencerProfile } from '../reducers/influencerProfile';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchInfluencerCampaignHistory, resetInfluencerCampaignHistory } from '../reducers/influencerCampaignHistory';
import { fetchInfluencerRelatedInfluencers, resetInfluencerRelatedInfluencers } from '../reducers/influencerRelatedInfluencers';
import { fetchInfluencerGraphs, resetInfluencerGraphs } from '../reducers/influencerGraphs';
import { fetchInfluencersMeta } from '../reducers/influencersMeta';
import {
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_ABOUT_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_HISTORY_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_ENGAGEMENT_RATE_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_FOLLOWER_GROWTH_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_TOP_HASHTAGS_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_ENGAGEMENT_RATE_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_FOLLOWER_GROWTH_CLICKED,
  EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_TOP_HASHTAGS_CLICKED,
  trackEventProfileActions,
  trackEventSimilarCreatorsCriteriaChangeActions
} from '../../tracking/utils/tracking';

import { t } from 'utils/language';
import { isAdmin, isInfluencerRealm } from 'utils/account';
import { getBrandProfileId, isBrandsFocusedOrganization } from 'utils/organization';
import { calculateStartEndDates } from 'utils/graphs/utils';
import { isMetricsStillFetching } from 'utils/helper/profile';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  getUserNameFromChannel
} from 'utils/channels';
import {
  PROFILE_DETAILS_TABS,
  PROFILE_GRAPHS_DATE_SELECTION_ITEMS,
  getDefaultProfileTab,
  getProfileTabs,
  hasMetrics,
  isMinimumCriteriaSelected,
  isProfilePageComplete
} from 'utils/influencers';
import { Querystring } from 'utils/url';

import type {
  Account,
  GraphOverTimeProfile,
  GraphOverTimeProfileItem,
  GraphOverTimeStartDates,
  InfluencerCampaignHistory,
  InfluencerProfile as InfluencerProfileAlias,
  InfluencerTile,
  Organization,
  Role,
  TimeframeResult
} from 'types';

import type { SyntheticEvent } from 'react';

const trackingEventsByIdentifier = {
  about: {
    default: EVENT_TYPE_INFLUENCER_PROFILE_TAB_ABOUT_CLICKED
  },
  instagram: {
    default: EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_CLICKED,
    engagementRate: EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_ENGAGEMENT_RATE_CLICKED,
    followers: EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_FOLLOWER_GROWTH_CLICKED,
    overview: EVENT_TYPE_INFLUENCER_PROFILE_TAB_INSTAGRAM_TOP_HASHTAGS_CLICKED
  },
  history: {
    default: EVENT_TYPE_INFLUENCER_PROFILE_TAB_HISTORY_CLICKED
  },
  youtube: {
    default: EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_CLICKED,
    engagementRate: EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_ENGAGEMENT_RATE_CLICKED,
    followers: EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_FOLLOWER_GROWTH_CLICKED,
    overview: EVENT_TYPE_INFLUENCER_PROFILE_TAB_YOUTUBE_TOP_HASHTAGS_CLICKED
  }
};

type OwnProps = {
  profileId: number,
  canEdit: boolean,
  actionComponent: React.ReactNode,
  activeTab: string
};

type StateProps = {
  filter: string,
  influencerProfile: InfluencerProfileAlias,
  currentOrganization: Organization,
  mapByOrganizationId: {
    [x: number]: Organization
  },
  currentAccount: Account,
  campaignHistory: InfluencerCampaignHistory,
  campaignHistoryLoading: boolean,
  influencerGraphs: GraphOverTimeProfile,
  relatedInfluencers: Array<InfluencerTile>,
  isLightweightProfileMaintainer: boolean,
  relatedInfluencersLoading: boolean,
  influencerProfileLoading: boolean,
  roles: Array<Role>
};

type DispatchProps = {
  dispatchFetchGraphs: (b: number, a: TimeframeResult) => void,
  dispatchResetGraphs: () => void,
  dispatchFetchInfluencersMeta: (b: number, a: number) => void,
  dispatchFetchInfluencerProfile: (a: number) => Promise<InfluencerProfileAlias>,
  dispatchResetInfluencerProfile: () => void,
  dispatchShowConfirmModal: (a: string) => void,
  dispatchResetInfluencerCampaignHistory: () => void,
  dispatchFetchInfluencerCampaignHistory: (c: number, b: number, a?: string | null) => void,
  dispatchResetInfluencerRelatedInfluencers: () => void,
  dispatchFetchInfluencerRelatedInfluencers: (b: number, a?: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  activeTabMain: string,
  activeTabDetails: string,
  graphDateSelectedId: string,
  dataLoaded: {
    [x: string]: boolean
  },
  relatedInfluencersSearchQuery: {
    enableDemographicConditions: boolean,
    enableAudienceConditions: boolean,
    enableChannelConditions: boolean
  }
};

class InfluencerProfile extends React.PureComponent<Props, State> {
  state = {
    activeTabMain: this.props.activeTab,
    activeTabDetails: PROFILE_DETAILS_TABS(this.props.activeTab)[0].identifier,
    graphDateSelectedId: 'three-months',
    dataLoaded: {
      history: false,
      instagram: false,
      multi: false
    },
    relatedInfluencersSearchQuery: {
      enableDemographicConditions: false,
      enableAudienceConditions: true,
      enableChannelConditions: false
    }
  };

  componentDidMount() {
    this.fetchInfluencerProfile(this.props);
    addUserAction({
      eventCategory: 'drop down',
      eventAction: 'navigate',
      stage: 'finish',
      destination: 'creator'
    });
  }

  componentDidUpdate(prevProps: Props): void {
    const activeTab = this.getActiveTab();
    const wasFilterUpdated = this.props.filter !== prevProps.filter;

    if (activeTab === 'history' && wasFilterUpdated) {
      this.loadCampaignHistory();
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetInfluencerProfile,
      dispatchResetGraphs,
      dispatchResetInfluencerCampaignHistory,
      dispatchResetInfluencerRelatedInfluencers
    } = this.props;

    dispatchResetInfluencerCampaignHistory();
    dispatchResetInfluencerRelatedInfluencers();
    dispatchResetInfluencerProfile();
    dispatchResetGraphs();
  }

  checkProfileIncompleteStatus = (profile: InfluencerProfileAlias): void => {
    const { roles } = this.props;

    if (!profile) {
      return;
    }

    const { dispatchShowConfirmModal, canEdit, currentAccount } = this.props;
    const profileComplete = isProfilePageComplete(profile);
    const route = `/content-creator/profiles/${profile.id}/edit`;

    if (!isAdmin(currentAccount, roles) && canEdit && !profileComplete) {
      dispatchShowConfirmModal(route);
    }
  };

  fetchGraphs(nextProps: Props): void {
    const { profileId, dispatchFetchGraphs } = nextProps;
    const { graphDateSelectedId } = this.state;
    const dateRange = calculateStartEndDates(PROFILE_GRAPHS_DATE_SELECTION_ITEMS, graphDateSelectedId);

    dispatchFetchGraphs(profileId, dateRange);

    this.setState(state => ({
      dataLoaded: {
        ...state.dataLoaded,
        multi: true
      }
    }));
  }

  fetchInfluencerProfile(nextProps: Props): void {
    const {
      profileId,
      actionComponent,
      currentOrganization,
      dispatchFetchInfluencersMeta,
      dispatchFetchInfluencerProfile
    } = nextProps;

    if (currentOrganization && actionComponent) {
      dispatchFetchInfluencersMeta(currentOrganization.id, profileId);
    }

    dispatchFetchInfluencerProfile(profileId)
      .then(profile => {
        const activeTab = this.getActiveTab();

        this.handleMainTabsSwitch(activeTab, 'activeTabMain', false, activeTab);
        this.checkProfileIncompleteStatus(profile);
      });
  }

  getActiveTab(): string {
    const { influencerProfile } = this.props;
    const { activeTabMain } = this.state;
    const hash = window.location.hash.replace(/#/, '');

    return hash || activeTabMain || getDefaultProfileTab(influencerProfile.channels) || 'about';
  }

  getGraphResult(activeTab: string): Array<GraphOverTimeProfileItem> {
    const { influencerGraphs } = this.props;
    const results = influencerGraphs.results.find(result => result[activeTab]);

    return results ? results[activeTab] : [];
  }

  getGraphStartDates(activeTab: string): GraphOverTimeStartDates {
    const { influencerGraphs } = this.props;
    const result = influencerGraphs.startDates[activeTab];

    return {
      ...influencerGraphs.startDates,
      [activeTab]: result
    };
  }

  handleRelatedCreatorSearchQueryChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { profileId, dispatchFetchInfluencerRelatedInfluencers } = this.props;
    const { currentTarget } = event;
    const name = currentTarget.name;
    const value = currentTarget.checked;

    if (!name) {
      return;
    }

    const relatedInfluencersSearchQuery = {
      ...this.state.relatedInfluencersSearchQuery,
      [name]: value
    };

    trackEventSimilarCreatorsCriteriaChangeActions(relatedInfluencersSearchQuery);

    this.setState((prevState) => ({
      ...prevState,
      relatedInfluencersSearchQuery
    }));


    if (isMinimumCriteriaSelected(relatedInfluencersSearchQuery)) {
      const conditions = Querystring.objectToString(relatedInfluencersSearchQuery);
      dispatchFetchInfluencerRelatedInfluencers(profileId, conditions);
    }
  };

  handleInfluencerProfileChanged = (): void => {
    this.fetchInfluencerProfile(this.props);
  };

  handleTabTracking(gaEvent: string): void {
    const { currentOrganization, influencerProfile } = this.props;

    if (gaEvent) {
      const isBrand = isBrandsFocusedOrganization(currentOrganization);
      const currentBrandId = getBrandProfileId(currentOrganization);
      const currentInfluencerId = influencerProfile?.id;
      const id = isBrand ? currentBrandId : currentInfluencerId;

      if (id) {
        trackEventProfileActions(gaEvent);
      }
    }
  }

  handleTabSwitch(type: keyof State, identifier?: string | null): void {
    if (isNil(identifier)) {
      return;
    }

    this.loadData(identifier);

    this.setState<never>({
      [type]: identifier
    });
  }

  handleMainTabsSwitch = (
    identifier: string | undefined | null,
    type: string | undefined | null,
    isActive: boolean,
    hash?: string | null
  ): void => {
    if (identifier && type && !isActive) {
      if (hash) {
        if (window.history.pushState) {
          window.history.pushState(null, '', `#${hash}`);
        } else {
          window.location.hash = hash; // fallback for (very) old browsers
        }
      }

      this.setState({
        activeTabDetails: 'followers'
      });

      const gaEvent = trackingEventsByIdentifier[identifier] && trackingEventsByIdentifier[identifier].default;
      this.handleTabTracking(gaEvent);
    }

    this.handleTabSwitch('activeTabMain', identifier);
  };

  handleSubTabsSwitch = (
    identifier: string | undefined | null,
    type: string | undefined | null,
    isActive: boolean
  ): void => {
    const { activeTabMain } = this.state;

    if (identifier && type && !isActive) {
      const gaEvent = trackingEventsByIdentifier[activeTabMain] && trackingEventsByIdentifier[activeTabMain][identifier];
      this.handleTabTracking(gaEvent);

      this.handleTabSwitch('activeTabDetails', identifier);
    }
  };

  handleGraphDateChange = (
    value: string
  ): void => {
    this.setState({
      graphDateSelectedId: value
    }, () => (
      this.fetchGraphs(this.props)
    ));
  };

  loadData(identifier?: string | null): void {
    const { dataLoaded } = this.state;

    if (identifier === CHANNEL_TYPE_INSTAGRAM && !dataLoaded.instagram) {
      this.loadInstagramData();
    }

    if ((identifier === CHANNEL_TYPE_INSTAGRAM || identifier === CHANNEL_TYPE_YOUTUBE || identifier === CHANNEL_TYPE_TIKTOK) && !dataLoaded.multi) {
      this.fetchGraphs(this.props);
    }

    if (identifier === 'history' && !dataLoaded.history) {
      this.loadCampaignHistory();
    }
  }

  loadInstagramData(): void {
    const { currentOrganization, profileId, dispatchFetchInfluencerRelatedInfluencers } = this.props;
    const { relatedInfluencersSearchQuery } = this.state;

    if (currentOrganization && isBrandsFocusedOrganization(currentOrganization)) {
      const conditions = Querystring.objectToString(relatedInfluencersSearchQuery);

      dispatchFetchInfluencerRelatedInfluencers(profileId, conditions);
    }

    this.setState(state => ({
      dataLoaded: {
        ...state.dataLoaded,
        instagram: true
      }
    }));
  }

  loadCampaignHistory(): void {
    const { currentOrganization, profileId, filter, dispatchFetchInfluencerCampaignHistory } = this.props;

    if (currentOrganization && isBrandsFocusedOrganization(currentOrganization)) {
      dispatchFetchInfluencerCampaignHistory(currentOrganization.id, profileId, filter);
    }

    this.setState(state => ({
      dataLoaded: {
        ...state.dataLoaded,
        history: true
      }
    }));
  }

  renderInfluencerNotAvailable(): React.ReactNode {
    return (
      <Box bordered>
        <BoxPart
          align="center"
          paddings="equal"
        >
          <section>
            <h1>404</h1>
            <p>
              {t('influencer_not_available_error')}
            </p>
          </section>
        </BoxPart>
      </Box>
    );
  }

  private renderChannelContent(): React.ReactNode {
    const {
      filter,
      influencerGraphs,
      influencerProfile,
      currentOrganization,
      campaignHistory,
      campaignHistoryLoading,
      relatedInfluencers,
      mapByOrganizationId,
      relatedInfluencersLoading
    } = this.props;

    const { activeTabDetails, graphDateSelectedId, relatedInfluencersSearchQuery } = this.state;

    const isFreemium = currentOrganization?.isFreemium;
    const updatedActiveTabMain = this.getActiveTab();
    const graphs = this.getGraphResult(updatedActiveTabMain);
    const graphStartDates = this.getGraphStartDates(updatedActiveTabMain);
    const selectedChannel = influencerProfile.channels.find(channel => channel.type === updatedActiveTabMain);
    const channelInfo = (updatedActiveTabMain === CHANNEL_TYPE_INSTAGRAM || updatedActiveTabMain === CHANNEL_TYPE_TIKTOK) && selectedChannel;
    const haveMetricsBeenGathered = (influencerProfile.channels).some(hasMetrics);
    // COLLAB-13712 & COLLAB-17556
    const isSpecialTempInfluencerProfileId = window.app.env.FEATURE_FACEBOOK_LOGIN;

    return (
      <React.Fragment>
        {
          (updatedActiveTabMain === CHANNEL_TYPE_INSTAGRAM
            || updatedActiveTabMain === CHANNEL_TYPE_YOUTUBE
            || updatedActiveTabMain === CHANNEL_TYPE_TIKTOK)
          && (
            <React.Fragment>
              <InfluencerChannelsCard
                key={`influencer-profile__channel-card--${updatedActiveTabMain}`}
                influencerProfile={influencerProfile}
                selectedChannel={selectedChannel}
                channelType={updatedActiveTabMain}
                haveMetricsBeenGathered={haveMetricsBeenGathered}
                isFreemium={isFreemium}
              />
              {isSpecialTempInfluencerProfileId ? null : (
                <InfluencerProfileTabsSection
                  key={updatedActiveTabMain}
                  activeChannel={updatedActiveTabMain}
                  influencerGraphs={graphs}
                  influencerGraphsStartDate={graphStartDates[updatedActiveTabMain]}
                  activeTab={activeTabDetails}
                  selectedDateRangeId={graphDateSelectedId}
                  selectedChannel={selectedChannel}
                  isLoading={influencerGraphs.isLoading}
                  onClick={this.handleSubTabsSwitch}
                  onChange={this.handleGraphDateChange}
                />
              )}
            </React.Fragment>
          )
        }
        {
          (
            updatedActiveTabMain === CHANNEL_TYPE_INSTAGRAM
            || updatedActiveTabMain === CHANNEL_TYPE_TIKTOK
          )
          && channelInfo && channelInfo.url
          && (
            <InfluencerAudienceData
              key={`audience-data-section-${updatedActiveTabMain}`}
              influencerProfile={influencerProfile}
              channelUserName={getUserNameFromChannel(channelInfo)}
              activeTabMain={updatedActiveTabMain}
              isFreemium={isFreemium}
            />
          )
        }
        {
          updatedActiveTabMain === 'history' && (
            <InfluencerCampaignHistoryTab
              filter={filter}
              influencerProfile={influencerProfile}
              campaignHistory={campaignHistory}
              campaignHistoryLoading={campaignHistoryLoading}
              mapByOrganizationId={mapByOrganizationId}
              isFreemium={isFreemium}
            />
          )
        }
        {
          updatedActiveTabMain === CHANNEL_TYPE_INSTAGRAM
          && isBrandsFocusedOrganization(currentOrganization)
          && !isSpecialTempInfluencerProfileId
          && (
            <div className="influencer-profile__related-influencers">
              <InfluencerProfileRelatedInfluencers
                relatedInfluencers={relatedInfluencers}
                relatedInfluencersSearchQuery={relatedInfluencersSearchQuery}
                onRelatedInfluencerSearchQueryChange={this.handleRelatedCreatorSearchQueryChange}
                isLoading={relatedInfluencersLoading}
              />
            </div>
          )
        }
      </React.Fragment>
    );
  }

  private renderDataFetchPlaceholder() {
    return <DataGatheringPlaceholder />;
  }

  render(): React.ReactNode {
    const {
      currentAccount,
      influencerProfile,
      canEdit,
      actionComponent,
      currentOrganization,
      isLightweightProfileMaintainer,
      influencerProfileLoading,
      roles
    } = this.props;

    if (!influencerProfile.id && influencerProfileLoading === false) {
      return this.renderInfluencerNotAvailable();
    }

    if (!influencerProfile.id) {
      return null;
    }

    const isFreemium = currentOrganization?.isFreemium;
    const updatedActiveTabMain = this.getActiveTab();
    const profileTabs = getProfileTabs(
      influencerProfile.channels,
      isBrandsFocusedOrganization(currentOrganization),
      isFreemium,
      window.app.env.FEATURE_FACEBOOK_LOGIN
    );
    const selectedChannel = influencerProfile.channels.find(channel => channel.type === updatedActiveTabMain);
    const haveMetricsBeenGathered = (influencerProfile.channels).some(hasMetrics);
    const isCurrentAccountAdmin = isAdmin(currentAccount, roles);
    const hasRealmRole = isInfluencerRealm(roles);

    const channelTab = updatedActiveTabMain === CHANNEL_TYPE_INSTAGRAM
      || updatedActiveTabMain === CHANNEL_TYPE_YOUTUBE
      || updatedActiveTabMain === CHANNEL_TYPE_TIKTOK;
    const isCurrentChannelStillGatheringMetrics = isMetricsStillFetching(selectedChannel?.metrics);

    const containerClasses = classnames({
      'influencer-profile': true
    });

    return (
      <section className={containerClasses}>
        {
          (isCurrentAccountAdmin || hasRealmRole) && (
            <React.Fragment>
              <ProfileAdminPanel
                isAdmin={isCurrentAccountAdmin}
                hasRealmRole={hasRealmRole}
                profile={influencerProfile}
                isLightweightProfileMaintainer={isLightweightProfileMaintainer}
                onChange={this.handleInfluencerProfileChanged}
              />
              <VerticalSpacing type="large" />
            </React.Fragment>
          )
        }
        <InfluencerProfileSummary
          canEdit={canEdit}
          inEditMode={false}
          influencerProfile={influencerProfile}
          actionComponent={actionComponent}
          haveMetricsBeenGathered={haveMetricsBeenGathered}
        />
        <div className="influencer-profile__tabs">
          <TabsSection
            withShadow
            items={profileTabs}
            activeTab={updatedActiveTabMain}
            onClick={this.handleMainTabsSwitch}
          />
        </div>
        {
          updatedActiveTabMain === 'about' && (
            <div className="influencer-profile__section">
              <InfluencerAboutTab
                influencerProfile={influencerProfile}
              />
            </div>
          )
        }
        {
          channelTab && isCurrentChannelStillGatheringMetrics
            ? this.renderDataFetchPlaceholder()
            : this.renderChannelContent()
        }
      </section>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const {
    brandsFilter,
    influencerProfile,
    organizations,
    account,
    influencerCampaignHistory,
    influencerGraphs,
    influencerRelatedInfluencers
  } = state;

  return ({
    filter: brandsFilter.activeFilter,
    influencerProfile: influencerProfile.profile,
    currentOrganization: organizations.currentOrganization,
    mapByOrganizationId: organizations.mapByOrganizationId,
    currentAccount: account.currentAccount,
    campaignHistory: influencerCampaignHistory.list,
    campaignHistoryLoading: influencerCampaignHistory.isLoading,
    influencerGraphs,
    relatedInfluencers: influencerRelatedInfluencers.list,
    relatedInfluencersLoading: influencerRelatedInfluencers.isLoading,
    influencerProfileLoading: influencerProfile.isLoading,
    isLightweightProfileMaintainer: isLightweightProfileMaintainerSelector(state),
    roles: account.roles
  });
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchGraphs: (influencerProfileId: number, dateRange: TimeframeResult) => (
    dispatch(fetchInfluencerGraphs(influencerProfileId, dateRange))
  ),

  dispatchResetGraphs: () => dispatch(resetInfluencerGraphs()),
  dispatchFetchInfluencersMeta: (organizationId: number, profileId: number) => dispatch(fetchInfluencersMeta([profileId])),
  dispatchFetchInfluencerProfile: (profileId: number) => dispatch(fetchInfluencerProfile(profileId)),
  dispatchResetInfluencerProfile: () => dispatch(resetInfluencerProfile()),

  dispatchShowConfirmModal: (route: string) => dispatch(showConfirmModal({
    onConfirm: () => {
      dispatch(hideConfirmModal());
      navigate.to(route);
    },
    onClose: () => {
      dispatch(hideConfirmModal());
    },
    confirmButtonLabel: t('influencer_profile_incomplete_modal_button'),
    title: t('influencer_profile_incomplete_modal_title'),
    message: t('influencer_profile_incomplete_modal_message')
  })),

  dispatchResetInfluencerCampaignHistory: () => dispatch(resetInfluencerCampaignHistory()),

  dispatchFetchInfluencerCampaignHistory: (currentOrganizationId, profileId, filter) =>
    dispatch(fetchInfluencerCampaignHistory(currentOrganizationId, profileId, { filter, includeMetrics: true })),

  dispatchResetInfluencerRelatedInfluencers: () => dispatch(resetInfluencerRelatedInfluencers()),
  dispatchFetchInfluencerRelatedInfluencers: (profileId, conditions) => dispatch(fetchInfluencerRelatedInfluencers(profileId, conditions))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerProfile);
